/**
 * Field Modal Component
 *
 * Purpose: Modal form for creating and editing field instances with validation.
 *          Handles field definition selection, model assignment, and override configuration.
 *
 * Architecture: Form-based modal with dependency loading (field definitions, models, existing fields).
 *              Supports both create and edit modes with GLOBAL override handling.
 *
 * Dependencies: React, Material-UI, MetadataService
 * Author: Development Team
 * Created: 2025-01-27
 * Extracted: 2025-01-21 (Refactoring)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Grid,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Public as GlobalIcon,
  Business as TenantIcon,
} from '@mui/icons-material';

import metadataService, {
  Field,
  FieldFormData,
  FieldDefinition,
  Model,
  PickerFilter,
  TenantInfo,
} from '../../../services/metadataService';
import DescriptionField, { descriptionLengthError } from '../../common/DescriptionField';
import { SecondaryButton, DestructiveButton } from '../../common/ActionButtons';
import DialogFooter from '../../common/DialogFooter';
import { invalidatePickerFilterGraph } from '../../../hooks/usePickerFilterGraph';
import {
  isFilterEnabled,
  isSourceExcluded,
  normalizeFilterConfig,
  withFilterEnabled,
  withSourceExcluded,
} from './fieldFilterConfig';

// Component interfaces
export interface FieldModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  field?: Field;
  tenantContext: TenantInfo;
  canEdit: boolean;
  effectiveCanEdit: boolean;
  parentFieldDefinition?: FieldDefinition | null;
  preSelectedModelId?: string; // Pre-populate model field when creating new field
  onClose: () => void;
  onSave: (data: FieldFormData) => Promise<void>;
  onDelete?: (field: Field) => Promise<void>;
}

/**
 * Modal component for create/edit field operations
 */
export const FieldModal: React.FC<FieldModalProps> = ({
  open,
  mode,
  field,
  tenantContext,
  canEdit: _canEdit,
  effectiveCanEdit,
  parentFieldDefinition,
  preSelectedModelId,
  onClose,
  onSave,
  onDelete,
}) => {
  const [formData, setFormData] = useState<FieldFormData>({
    fielddef_id: '',
    tenant_id: tenantContext.tenant_id,
    model_id: '',
    storage_type: 'metadata', // Always metadata for UI-created fields
    label_override: '',
    description_override: null,
    is_required: false,
    is_multiselect: false,
    allow_select_all: true,
    storage_decimal_places_override: null,
    display_decimal_places_override: null,
    status_override: undefined,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fieldDefinitions, setFieldDefinitions] = useState<FieldDefinition[]>([]);
  const [models, setModels] = useState<{ group: string; options: Model[] }[]>([]);
  const [loadingDependencies, setLoadingDependencies] = useState(true);
  const [existingFields, setExistingFields] = useState<Field[]>([]);
  // ORCH-400 §3.5: the candidate sources that narrow this list Field (edit mode only)
  const [pickerFilter, setPickerFilter] = useState<PickerFilter | null>(null);
  const [loadingPickerFilter, setLoadingPickerFilter] = useState(false);

  const isListField =
    (fieldDefinitions.find((fd) => fd.fielddef_id === formData.fielddef_id)?.datatype ||
      field?.field_definition?.datatype) === 'list';
  const showFilteredBy = mode === 'edit' && isListField;

  // Load dependencies (field definitions and models)
  useEffect(() => {
    const loadDependencies = async () => {
      try {
        setLoadingDependencies(true);
        const [fieldDefsResponse, normalModels, fielddefModels, existingFieldsData] =
          await Promise.all([
            metadataService.getFieldDefinitions({ tenant_id: tenantContext.tenant_id }),
            metadataService.getNormalModels(),
            metadataService.getFieldDefinitionModels(),
            metadataService.getFields({ tenant_id: tenantContext.tenant_id }),
          ]);
        setFieldDefinitions(fieldDefsResponse || []);
        setExistingFields(existingFieldsData || []);

        // Create grouped options for the dropdown
        const groupedModels = [
          {
            group: 'Business Models',
            options: (normalModels || []).sort((a, b) => a.name.localeCompare(b.name)),
          },
          {
            group: 'Field Definition Models',
            options: (fielddefModels || []).sort((a, b) => a.name.localeCompare(b.name)),
          },
        ];
        setModels(groupedModels);
      } catch (error) {
        console.error('Failed to load dependencies:', error);
      } finally {
        setLoadingDependencies(false);
      }
    };

    if (open) {
      loadDependencies();
    }
  }, [open, tenantContext.tenant_id]);

  // ORCH-400 §4.7: the Filtered by section reads the model's filter graph
  useEffect(() => {
    // Field rows from GET /fields carry no nested definition, so the list check comes
    // from the loaded field definitions (showFilteredBy), not from field.field_definition
    if (!open || !showFilteredBy || !field) {
      setPickerFilter(null);
      return;
    }
    let cancelled = false;
    const loadPickerFilter = async () => {
      try {
        setLoadingPickerFilter(true);
        const graph = await metadataService.getPickerFilters(field.model_id);
        if (!cancelled) {
          setPickerFilter(graph.fields.find((f) => f.fielddef_id === field.fielddef_id) ?? null);
        }
      } catch (error) {
        console.error('Failed to load picker filters:', error);
        if (!cancelled) setPickerFilter(null);
      } finally {
        if (!cancelled) setLoadingPickerFilter(false);
      }
    };
    loadPickerFilter();
    return () => {
      cancelled = true;
    };
  }, [open, showFilteredBy, field]);

  // Initialize form data when modal opens
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && field) {
        setFormData({
          fielddef_id: field.fielddef_id,
          tenant_id: field.tenant_id,
          model_id: field.model_id,
          storage_type: field.storage_type,
          label_override: field.label_override || '',
          description_override: field.description_override || null,
          is_required: field.is_required || false,
          is_multiselect: field.is_multiselect || false,
          allow_select_all: field.allow_select_all !== false,
          filter_config: normalizeFilterConfig(field.filter_config),
          storage_decimal_places_override: field.storage_decimal_places_override ?? null,
          display_decimal_places_override: field.display_decimal_places_override ?? null,
          status_override: field.status_override,
        });
      } else {
        const formData: FieldFormData = {
          fielddef_id: '',
          tenant_id: tenantContext.tenant_id,
          model_id: preSelectedModelId || '', // Use pre-selected model if provided
          storage_type: 'metadata' as const, // Always metadata for UI-created fields
          label_override: '',
          description_override: null,
          is_required: false,
          is_multiselect: false,
          allow_select_all: true,
          storage_decimal_places_override: null,
          display_decimal_places_override: null,
          status_override: undefined,
        };
        setFormData(formData);
      }
      setErrors({});
    }
  }, [open, mode, field, tenantContext, preSelectedModelId]);

  // Validate form data
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.fielddef_id.trim()) {
      newErrors.fielddef_id = 'Field definition is required';
    }

    if (!formData.model_id.trim()) {
      newErrors.model_id = 'Model is required';
    }

    if (formData.label_override && formData.label_override.length > 100) {
      newErrors.label_override = 'Label override must be under 100 characters';
    }

    const descOverrideError = descriptionLengthError(formData.description_override);
    if (descOverrideError) newErrors.description_override = descOverrideError;

    // Validate override decimal places if field definition is numeric type
    const selectedFieldDef = fieldDefinitions.find((fd) => fd.fielddef_id === formData.fielddef_id);
    const datatype = selectedFieldDef?.datatype || field?.field_definition?.datatype;
    const isNumeric = datatype === 'currency' || datatype === 'decimal' || datatype === 'percent';

    if (isNumeric) {
      // Override decimal places are optional (can be null to inherit), but if provided must be valid
      if (
        formData.storage_decimal_places_override !== null &&
        formData.storage_decimal_places_override !== undefined
      ) {
        if (
          formData.storage_decimal_places_override < 0 ||
          formData.storage_decimal_places_override > 10
        ) {
          newErrors.storage_decimal_places_override =
            'Storage decimal places must be between 0 and 10';
        }
      }

      if (
        formData.display_decimal_places_override !== null &&
        formData.display_decimal_places_override !== undefined
      ) {
        if (
          formData.display_decimal_places_override < 0 ||
          formData.display_decimal_places_override > 10
        ) {
          newErrors.display_decimal_places_override =
            'Display decimal places must be between 0 and 10';
        }
      }
    }

    // Check for duplicate fields (only in create mode)
    if (mode === 'create' && formData.fielddef_id && formData.model_id) {
      const existingField = existingFields.find(
        (field) =>
          field.fielddef_id === formData.fielddef_id &&
          field.model_id === formData.model_id &&
          field.tenant_id === formData.tenant_id &&
          // Only check for active fields (not deleted ones)
          (field.status_override !== 'deleted' || field.status_override === null)
      );

      if (existingField) {
        const fieldDef = fieldDefinitions.find((fd) => fd.fielddef_id === formData.fielddef_id);
        const fieldLabel = fieldDef?.label || formData.fielddef_id;
        newErrors.fielddef_id = `Field '${fieldLabel}' already exists in this model. Each field definition can only be added once per model.`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      // Clean up form data - remove empty strings and convert to undefined
      const cleanData = {
        ...formData,
        label_override: formData.label_override?.trim() || undefined,
        description_override: formData.description_override?.trim() || undefined,
        is_required: formData.is_required || undefined,
        is_multiselect: formData.is_multiselect || undefined,
        // ORCH-418: false is meaningful (turns "All" off), so it is always sent
        allow_select_all: formData.allow_select_all !== false,
        // ORCH-400: null is meaningful too (back to "every source filters"), so a list
        // Field in edit mode always sends its config; other fields leave it alone
        filter_config: showFilteredBy ? normalizeFilterConfig(formData.filter_config) : undefined,
        storage_decimal_places_override: formData.storage_decimal_places_override ?? undefined,
        display_decimal_places_override: formData.display_decimal_places_override ?? undefined,
        status_override: formData.status_override || undefined,
      };
      await onSave(cleanData);
      if (showFilteredBy) invalidatePickerFilterGraph();
      onClose();
    } catch (error) {
      console.error('Failed to save field:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!field || !onDelete) return;

    try {
      setLoading(true);
      await onDelete(field);
      onClose();
    } catch (error) {
      console.error('Failed to delete field:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {tenantContext.is_global ? <GlobalIcon /> : <TenantIcon />}
          {mode === 'create' ? 'Create Field' : 'Edit Field'}
          <Chip
            label={tenantContext.name}
            size="small"
            color={tenantContext.is_global ? 'warning' : 'primary'}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {loadingDependencies ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {tenantContext.is_global && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are creating/editing a GLOBAL field. This will affect all tenants.
              </Alert>
            )}

            {!tenantContext.is_global && field?.tenant_id === 'GLOBAL' && mode === 'edit' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                You are editing a GLOBAL field. This will create a tenant-specific override in your
                current tenant instead of modifying the GLOBAL field.
              </Alert>
            )}

            {/* Show appropriate message for related fields based on parent field definition access level */}
            {!tenantContext.is_global && parentFieldDefinition && (
              <>
                {parentFieldDefinition.access === '0' && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    This field definition is internal only and not accessible in your tenant
                    context. Related fields cannot be created or modified.
                  </Alert>
                )}
                {parentFieldDefinition.access === '1' && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This field definition is read-only in your tenant context. Related fields cannot
                    be created or modified.
                  </Alert>
                )}
                {parentFieldDefinition.access === '2' && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    You are managing related fields for a GLOBAL field definition. Changes will be
                    specific to your tenant.
                  </Alert>
                )}
                {parentFieldDefinition.access === '3' && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    You are managing related fields for a GLOBAL field definition. Changes will be
                    specific to your tenant.
                  </Alert>
                )}
              </>
            )}

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  options={fieldDefinitions}
                  getOptionLabel={(option) => `${option.label} (${option.fielddef_id})`}
                  value={
                    fieldDefinitions.find((fd) => fd.fielddef_id === formData.fielddef_id) || null
                  }
                  onChange={(_event, newValue) => {
                    setFormData({
                      ...formData,
                      fielddef_id: newValue?.fielddef_id || '',
                      // Keep the current tenant_id - don't override it with the field definition's tenant_id
                    });
                  }}
                  disabled={mode === 'edit' || !effectiveCanEdit}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Field Definition"
                      error={!!errors.fielddef_id}
                      helperText={
                        errors.fielddef_id || 'Select a field definition to use as template'
                      }
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body2">{option.label}</Typography>
                        <Typography variant="caption" color="textSecondary" fontFamily="monospace">
                          {option.fielddef_id} ({metadataService.getDatatypeLabel(option.datatype)})
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  options={models.flatMap((group) => group.options)}
                  groupBy={(option) => {
                    const group = models.find((g) => g.options.includes(option));
                    return group?.group || '';
                  }}
                  getOptionLabel={(option) => option.name}
                  value={
                    models
                      .flatMap((group) => group.options)
                      .find((m) => m.model_id === formData.model_id) || null
                  }
                  onChange={(_event, newValue) => {
                    setFormData({ ...formData, model_id: newValue?.model_id || '' });
                  }}
                  disabled={!effectiveCanEdit}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Model"
                      error={!!errors.model_id}
                      helperText={errors.model_id || 'Select the model this field belongs to'}
                      data-testid="model-field"
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Label Override"
                  value={formData.label_override}
                  onChange={(e) => setFormData({ ...formData, label_override: e.target.value })}
                  error={!!errors.label_override}
                  helperText={
                    errors.label_override ||
                    'Optional custom label (leave empty to use field definition label)'
                  }
                  fullWidth
                  disabled={!effectiveCanEdit}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <DescriptionField
                  label="Description Override"
                  value={formData.description_override || ''}
                  onChange={(value) =>
                    setFormData({ ...formData, description_override: value || null })
                  }
                  error={!!errors.description_override}
                  helperText={
                    errors.description_override ||
                    'Optional description override (leave empty to inherit from field definition)'
                  }
                  fullWidth
                  disabled={!effectiveCanEdit}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_required || false}
                      onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                      disabled={!effectiveCanEdit}
                    />
                  }
                  label="Required Field"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_multiselect || false}
                      onChange={(e) =>
                        setFormData({ ...formData, is_multiselect: e.target.checked })
                      }
                      disabled={!effectiveCanEdit}
                    />
                  }
                  label="Multi-Select"
                />
              </Grid>

              {/* ORCH-418: a multi-select list field offers "All" unless this is off */}
              {formData.is_multiselect && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.allow_select_all !== false}
                        onChange={(e) =>
                          setFormData({ ...formData, allow_select_all: e.target.checked })
                        }
                        disabled={!effectiveCanEdit}
                        data-testid="field-allow-select-all"
                      />
                    }
                    label="Allow All"
                  />
                </Grid>
              )}

              {/* ORCH-400 §3.5: which fields narrow this list Field's picker and save check */}
              {showFilteredBy && (
                <Grid size={{ xs: 12 }} data-testid="field-filtered-by">
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Filtered by
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isFilterEnabled(formData.filter_config)}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            filter_config: withFilterEnabled(
                              formData.filter_config,
                              e.target.checked
                            ),
                          })
                        }
                        disabled={!effectiveCanEdit}
                        data-testid="field-filter-enabled"
                      />
                    }
                    label="Filter choices by related fields"
                  />
                  <Typography variant="caption" color="textSecondary" display="block">
                    Security access and the tenant&apos;s enabled lists always apply.
                  </Typography>
                  {loadingPickerFilter ? (
                    <CircularProgress size={16} sx={{ mt: 1 }} />
                  ) : !pickerFilter || pickerFilter.sources.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mt: 1 }}
                      data-testid="field-filter-empty"
                    >
                      No fields filter this field
                    </Typography>
                  ) : (
                    <Box display="flex" flexDirection="column" sx={{ mt: 1, pl: 2 }}>
                      {pickerFilter.sources.map((source) => (
                        <FormControlLabel
                          key={`${source.model_id}:${source.fielddef_id}`}
                          control={
                            <Checkbox
                              checked={!isSourceExcluded(formData.filter_config, source)}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  filter_config: withSourceExcluded(
                                    formData.filter_config,
                                    source,
                                    !e.target.checked
                                  ),
                                })
                              }
                              disabled={
                                !effectiveCanEdit || !isFilterEnabled(formData.filter_config)
                              }
                              data-testid={`field-filter-source-${source.model_id}-${source.fielddef_id}`}
                            />
                          }
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2">
                                {source.field_label ?? source.fielddef_id}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {source.model_id === formData.model_id
                                  ? 'This model'
                                  : (source.model_label ?? source.model_id)}
                              </Typography>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={source.kind === 'related' ? 'Related field' : 'Same list'}
                              />
                            </Box>
                          }
                        />
                      ))}
                    </Box>
                  )}
                </Grid>
              )}

              {/* Storage Decimal Places Override - only show for numeric datatypes */}
              {(() => {
                const selectedFieldDef = fieldDefinitions.find(
                  (fd) => fd.fielddef_id === formData.fielddef_id
                );
                const datatype = selectedFieldDef?.datatype || field?.field_definition?.datatype;
                const isNumeric =
                  datatype === 'currency' || datatype === 'decimal' || datatype === 'percent';

                return isNumeric ? (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Storage Decimal Places Override"
                      type="number"
                      value={formData.storage_decimal_places_override ?? ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                        setFormData({ ...formData, storage_decimal_places_override: value });
                      }}
                      error={!!errors.storage_decimal_places_override}
                      helperText={
                        errors.storage_decimal_places_override ||
                        'Override storage decimal places (0-10, leave empty to inherit)'
                      }
                      fullWidth
                      inputProps={{ min: 0, max: 10 }}
                      disabled={!effectiveCanEdit}
                    />
                  </Grid>
                ) : null;
              })()}

              {/* Display Decimal Places Override - only show for numeric datatypes */}
              {(() => {
                const selectedFieldDef = fieldDefinitions.find(
                  (fd) => fd.fielddef_id === formData.fielddef_id
                );
                const datatype = selectedFieldDef?.datatype || field?.field_definition?.datatype;
                const isNumeric =
                  datatype === 'currency' || datatype === 'decimal' || datatype === 'percent';

                return isNumeric ? (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Display Decimal Places Override"
                      type="number"
                      value={formData.display_decimal_places_override ?? ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                        setFormData({ ...formData, display_decimal_places_override: value });
                      }}
                      error={!!errors.display_decimal_places_override}
                      helperText={
                        errors.display_decimal_places_override ||
                        'Override display decimal places (0-10, leave empty to inherit)'
                      }
                      fullWidth
                      inputProps={{ min: 0, max: 10 }}
                      disabled={!effectiveCanEdit}
                    />
                  </Grid>
                ) : null;
              })()}
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogFooter
        destructiveAction={
          mode === 'edit' &&
          onDelete &&
          effectiveCanEdit && (
            <DestructiveButton
              onClick={handleDelete}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              Delete
            </DestructiveButton>
          )
        }
      >
        <SecondaryButton onClick={onClose} disabled={loading}>
          Cancel
        </SecondaryButton>

        {effectiveCanEdit && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || loadingDependencies}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
};

export default FieldModal;
